import { Callout } from './Callout';

interface ClarityRecordingsProps {
  /** Clarity project id, e.g. "xtwrc7x9e9". Absent = Clarity not connected. */
  projectId?: string;
  /** Scopes the deep-link to one page where Clarity supports it. */
  pagePath?: string;
}

/**
 * Session recordings and heatmaps — via deep-link, because they cannot be fetched.
 *
 * <b>Clarity does record video, and the API cannot serve it.</b> The Data Export API returns
 * aggregate metrics only: there is no recordings endpoint, no heatmap endpoint, and no per-session
 * detail at any tier. That is a limit of Microsoft's API, not of this dashboard, and no amount of
 * work here changes it.
 *
 * So this links to the real thing rather than faking it. The alternative — rendering an empty
 * "recordings" panel that never fills — would imply the data is coming, which it is not.
 *
 * The control plane's own click heatmap on the Live tab is the first-party equivalent for the
 * *aggregate* question ("where do people click"). It is deliberately NOT a replacement for watching
 * one real session, which only Clarity's own player does.
 */
export function ClarityRecordings({ projectId, pagePath }: ClarityRecordingsProps) {
  if (!projectId) {
    return (
      <Callout tone="info" title="Clarity not connected">
        Add a Clarity source on the Sources page to enable session recordings.
      </Callout>
    );
  }

  const base = `https://clarity.microsoft.com/projects/view/${encodeURIComponent(projectId)}`;

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>Session recordings &amp; heatmaps</h2>
        <span className="t-label subtle">opens in Clarity</span>
      </div>
      <div className="card-body">
        <p className="t-body muted" style={{ marginTop: 0 }}>
          Clarity records real sessions as replayable video, but its Data Export API serves
          aggregate metrics only — there is no endpoint for recordings or heatmaps at any tier.
          These open Clarity's own player, where the video actually lives.
        </p>

        <div className="clarity-links">
          <a className="btn btn-primary" href={`${base}/recordings`} target="_blank" rel="noreferrer noopener">
            Watch recordings
          </a>
          <a className="btn btn-ghost" href={`${base}/heatmaps`} target="_blank" rel="noreferrer noopener">
            Clarity heatmaps
          </a>
          <a className="btn btn-ghost" href={`${base}/dashboard`} target="_blank" rel="noreferrer noopener">
            Clarity dashboard
          </a>
        </div>

        {pagePath && (
          <p className="t-label subtle" style={{ marginBottom: 0 }}>
            Filter to <code>{pagePath}</code> inside Clarity — its URL filters are not addressable
            from outside, so the link cannot pre-apply them.
          </p>
        )}

        <p className="t-label subtle" style={{ marginBottom: 0 }}>
          For the aggregate version of "where do people click", the Live tab's click map is
          first-party and realtime — but it is not a substitute for watching one real session.
        </p>
      </div>
    </div>
  );
}
